// @ts-nocheck
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import JoditEditor from 'jodit-react'
import { Jodit } from 'jodit'
import styled, { css } from 'styled-components'
import { convertPreviousSqrt, getSqrtHtml } from 'util/convertSqrt'
import { RICH_CONTENT_FONT_FAMILY } from './HtmlParser'
import { ElementTohtml, htmlToElement } from './htmlToElement'

interface IRichEditor {
  value: string
  onChange: (value: string) => void
  minHeight?: string
  setIsSaveEnable?: Function
  disabled?: boolean
}

const EditorShell = styled.div<{ $disabled?: boolean }>`
  ${(props) =>
    props.$disabled &&
    css`
      .jodit-toolbar__box,
      .jodit-wysiwyg {
        pointer-events: none;
      }
    `}
`

const RichEditor: FC<IRichEditor> = ({
  value,
  onChange,
  minHeight,
  setIsSaveEnable,
  disabled = false
}: IRichEditor) => {
  const [content, setContent] = useState<string>(value || '')
  const [showModal, setShowModal] = useState<boolean>(false)

  const imagePropUpdatedRef = useRef<boolean>(false)
  const isEditorFocusedRef = useRef(false)

  // Move helper functions before EditConfig

  const isBlockElement = (element) => {
    try {
      const cStyle =
        element.currentStyle || window.getComputedStyle(element, '')
      return cStyle.display === 'block'
    } catch {
      return false
    }
  }

  const getStylesByTag = (tag: string) => {
    switch (tag) {
      case 'h1':
        return {
          fontSize: '20px',
          color: '#4B4B4B',
          bold: true,
          italic: false
        }
      case 'h2':
        return {
          fontSize: '19px',
          color: '#4B4B4B',
          bold: true,
          italic: false
        }
      case 'h3':
        return {
          fontSize: '19px',
          color: '#4B4B4B',
          bold: false,
          italic: false
        }
      case 'blockquote':
        return {
          fontSize: '18px',
          color: '#4B4B4B',
          bold: false,
          italic: true
        }
      default:
        return {
          fontSize: '19px',
          color: '#4B4B4B',
          bold: false,
          italic: false
        }
    }
  }

  // Sync external value only when editor is not actively being edited.
  useEffect(() => {
    if (disabled) {
      isEditorFocusedRef.current = false
    } else if (isEditorFocusedRef.current) {
      return
    }

    const incomingValue = value || ''

    if (incomingValue === content) {
      return
    }

    const formattedIncomingValue = formatContant(incomingValue)
    setContent((prevContent) =>
      prevContent === formattedIncomingValue
        ? prevContent
        : formattedIncomingValue
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, value])

  const handlePaste = (e: ClipboardEvent) => {
    const items = (e.clipboardData || e.originalEvent.clipboardData).items
    let isImage = false
    const arr = [...items]
    arr.forEach((item: any) => {
      if (!isImage && item.type.includes('image')) {
        isImage = true
      }
    })

    if (isImage) {
      setShowModal(true)
      return false
    }

    const html = (e.clipboardData || e.originalEvent.clipboardData).getData(
      'text/html'
    )

    if (!html) return

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const images = doc.querySelectorAll('img')

    if (!images.length) return

    setShowModal(true)
    return false
  }

  const EditConfig = useMemo(
    () => ({
      readonly: disabled,
      toolbar: true,
      /* Pase From Word Plugin Config Start */
      askBeforePasteFromWord: true,
      processPasteFromWord: true,
      defaultActionOnPasteFromWord: Jodit.constants.INSERT_AS_TEXT,
      /* Pase From Word Plugin Config End */
      defaultFontSizePoints: 'px',
      placeholder: '',
      removeButtons: [
        'video',
        'file',
        'print',
        'link',
        'source',
        'about',
        'image'
      ],
      removeButtonsMD: [
        'video',
        'file',
        'print',
        'link',
        'source',
        'about',
        'image'
      ],
      removeButtonsSM: [
        'video',
        'file',
        'print',
        'link',
        'source',
        'about',
        'image'
      ],
      removeButtonsXS: [
        'video',
        'file',
        'print',
        'link',
        'source',
        'about',
        'image'
      ],
      buttons: `paragraph,font,fontsize,bold,italic,underline,strikethrough,brush,eraser,ul,ol,align,lineHeight,superscript,subscript,addFraction,addSqrt,spellcheck,cut,source,table`,
      minHeight: minHeight,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      autofocus: false,
      toolbarAdaptive: false,
      askBeforePasteHTML: false,
      controls: {
        font: {
          list: Jodit.atom({
            Nunito: 'Nunito',
            Arimo: 'Arimo',
            'Roboto Medium,Arial,sans-serif': 'Roboto',
            'Helvetica,sans-serif': 'Helvetica',
            'Arial,Helvetica,sans-serif': 'Arial',
            'Georgia,Merriweather,serif': 'Georgia',
            'Impact,Anton,Charcoal,sans-serif': 'Impact',
            'Tahoma,Noto Sans,Geneva,sans-serif': 'Tahoma',
            "'Times New Roman',Tinos,Times,serif": 'Times New Roman',
            'Verdana,Open Sans,Geneva,sans-serif': 'Verdana'
          })
        },
        fontsize: {
          list: Jodit.atom([
            8, 9, 10, 11, 12, 14, 16, 18, 19, 24, 30, 36, 48, 60, 72, 96
          ])
        },
        addFraction: {
          name: 'Add Fraction',
          tooltip: 'Add Fraction',
          iconURL: '/half.png',
          exec: (editor) => {
            editor.selection.insertHTML(`
                    <div class="math_frac">
                        <span>
                            <b>1</b>
                        </span>
                        <b>
                            <span class="symbol">/</span>
                            <span class="bottom">
                                <b>2</b>
                            </span>
                        </b>
                    </div>`)
          }
        },
        addSqrt: {
          name: 'Add Square root',
          tooltip: 'Add Square root',
          iconURL: '/square-root.svg',
          exec: (editor) => {
            console.log('addSqrt', editor)
            const sqrtHtml = getSqrtHtml('2xy')
            editor.selection.insertHTML(`
                    ${sqrtHtml}`)
          }
        },
        paragraph: {
          list: {
            h1: 'Heading 1',
            h2: 'Heading 2',
            h3: 'Heading 3',
            h4: 'Heading 4',
            blockquote: 'Byline',
            p: 'Body'
          },
          exec: (editor, current, { control }) => {
            const tag = control.args && control.args[0]
            const focusNode = window.getSelection().focusNode

            const blockNode = isBlockElement(focusNode)
              ? focusNode
              : focusNode?.parentNode
            const editorelement = document.querySelector(
              '.jodit-react-container'
            )

            if (editorelement?.contains(blockNode)) {
              const { fontSize, color, bold, italic } = getStylesByTag(tag)

              if (blockNode) {
                blockNode.style.fontSize = fontSize
                blockNode.style.color = color
                blockNode.style.fontFamily = RICH_CONTENT_FONT_FAMILY
                blockNode.style.fontWeight = bold ? 'bold' : 'normal'
                blockNode.style.fontStyle = italic ? 'italic' : 'normal'
              }
            }

            return false
          }
        }
      },
      style: {
        fontFamily: RICH_CONTENT_FONT_FAMILY,
        fontSize: '19px'
      },
      events: {
        beforePaste: handlePaste
      },
      cleanHTML: {
        fillEmptyParagraph: false
      }
    }),
    [disabled, minHeight]
  )

  // Remove the useLayoutEffect - no longer needed

  const cleaFraction = (elem: any) => {
    elem.childNodes.forEach((item) => {
      if (item.nodeType === 3) {
        item.textContent = item.textContent.trim()
        return
      } else {
        cleaFraction(item)
      }
    })

    return
  }

  const formatContant = (inputContent: string) => {
    const safeInputContent =
      typeof inputContent === 'string' ? inputContent : ''

    let div

    try {
      div = htmlToElement(safeInputContent)
    } catch {
      return safeInputContent
    }

    const math = div.querySelectorAll('.math_frac')

    convertPreviousSqrt(div)

    math.forEach((fraction) => {
      cleaFraction(fraction)
      const prevElement = fraction.previousElementSibling
      const nextElement = fraction.nextElementSibling
      if (
        prevElement &&
        prevElement.nodeName === 'P' &&
        !prevElement.className?.includes('math_frac_inline')
      ) {
        prevElement?.classList.add('math_frac_inline')
      }
      if (
        nextElement &&
        nextElement.nodeName === 'P' &&
        !nextElement.className?.includes('math_frac_inline')
      ) {
        nextElement?.classList.add('math_frac_inline')
      }

      const isSpanRequired = ['spanNextToFraction', 'math_frac_inline'].some(
        (className) => nextElement?.className.includes(className)
      )
      console.log({ isSpanRequired })
      if (!nextElement || !isSpanRequired) {
        console.log('Inserting')
        const newSpanElement = document.createElement('span')
        newSpanElement.classList.add('spanNextToFraction')
        newSpanElement.innerText = '.'
        fraction.insertAdjacentElement('afterend', newSpanElement)
      }
    })

    const sqrtElems = div.querySelectorAll('.sqr-root')
    sqrtElems.forEach((item) => {
      cleaFraction(item)
      if (!item.nextSibling) {
        const newtext = document.createTextNode('\u00A0')
        item.parentNode?.appendChild(newtext)
      } else if (item.nextSibling && !item.nextSibling.textContent) {
        const newtext = document.createTextNode('\u00A0')
        item.parentNode.insertBefore(newtext, item.nextSibling)
      }

      if (item.nextElementSibling) {
        item.nextElementSibling?.classList.add('sqr-root_inline')
      }

      if (item.previousElementSibling) {
        item.previousElementSibling.classList.add('sqr-root_inline')
      }
    })

    return ElementTohtml(div)
  }

  const handleEditorFocus = () => {
    if (disabled) return

    isEditorFocusedRef.current = true
  }

  const handleEditorChange = (nextContent: string) => {
    if (disabled) return

    setIsSaveEnable && setIsSaveEnable()
    setContent((prevContent) =>
      prevContent === nextContent ? prevContent : nextContent
    )
    onChange(nextContent)
  }

  const handleEditorBlur = (nextContent: string) => {
    if (disabled) return

    isEditorFocusedRef.current = false

    const formattedContent = formatContant(nextContent || '')

    setContent((prevContent) =>
      prevContent === formattedContent ? prevContent : formattedContent
    )

    if ((value || '') !== formattedContent) {
      onChange(formattedContent)
    }
  }

  const makeParaInlineWithFraction = () => {
    const joditTextParas = document.querySelectorAll(
      '.jodit-wysiwyg > .math_frac'
    )
    if (joditTextParas.length) {
      joditTextParas.forEach((item) => {
        if (
          item.nextSibling?.nodeName === 'P' &&
          !item.nextSibling?.className?.includes('math_frac_inline')
        ) {
          item.nextSibling?.classList.add('math_frac_inline')
        }

        if (
          item.previousSibling?.nodeName === 'P' &&
          !item.previousSibling?.className?.includes('math_frac_inline')
        ) {
          item.previousSibling?.classList.add('math_frac_inline')
        }
      })
    }
  }

  useEffect(() => {
    if (!content) return

    const handler = (e) => {
      setTimeout(() => {
        const dialogBox = document.querySelector('.jodit-dialog__panel')
        if (!dialogBox) return
        imagePropUpdatedRef.current = true

        const modalOpned = document.querySelector('.fade.modal.show')
        if (modalOpned) {
          modalOpned.style.display = 'none'
        }

        const applyBtn = document.querySelector('.jodit-ui-button_ok')
        if (!applyBtn) return

        applyBtn.addEventListener('click', () => {
          imagePropUpdatedRef.current = false
          const modalOpned = document.querySelector('.fade.modal.show')
          if (modalOpned) {
            modalOpned.style.display = 'block'
          }
        })

        const closeBtn = dialogBox.querySelector(
          '.jodit-toolbar-button_close > button'
        )

        closeBtn?.addEventListener('click', () => {
          imagePropUpdatedRef.current = false
          const modalOpned = document.querySelector('.fade.modal.show')
          if (modalOpned) {
            modalOpned.style.display = 'block'
          }
        })

        const deleteBtn = dialogBox.querySelector('.jodit-ui-button_bin')
        deleteBtn?.addEventListener('click', () => {
          imagePropUpdatedRef.current = false
          const modalOpned = document.querySelector('.fade.modal.show')
          if (modalOpned) {
            modalOpned.style.display = 'block'
          }
        })
      }, 100)
    }
    document.body.addEventListener('click', handler)

    makeParaInlineWithFraction()

    return () => {
      document.body.removeEventListener('click', handler)
    }
  }, [content])

  return (
    <>
      <Row className='w-100 mx-auto'>
        <Col className='text-white p-0'>
          <EditorShell $disabled={disabled} aria-disabled={disabled}>
            <JoditEditor
              value={content}
              config={EditConfig}
              onFocus={handleEditorFocus}
              onChange={handleEditorChange}
              onBlur={handleEditorBlur}
              tabIndex={1}
            />
          </EditorShell>
        </Col>
      </Row>
    </>
  )
}

export default RichEditor
